import { useMemo } from 'react';
import { useUnit } from 'effector-react';
import { useTranslation } from 'react-i18next';
import { $dashboardNotificationStore, $profileFeatureStore } from '@lib/store';
import { ArrayUtils, DateUtils, RoutePaths } from '@shared/utils';
import type { Notification } from '@shared/api/rest/dashboards';
import { Button, Modal, NoData, Text } from '@shared/widgets';

interface NotificationsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const NotificationsModal = (props: NotificationsModalProps) => {
  const profileFeatureStore = useUnit($profileFeatureStore.store);
  const dashboardNotificationStore = useUnit($dashboardNotificationStore.store);

  const { t } = useTranslation();

  const onClearAllNotifications = () => $dashboardNotificationStore.events.delete({ allNotifications: true });
  const onMarkAllAsReadNotifications = () => $dashboardNotificationStore.events.markAllAsRead({ allNotifications: true });

  const groupedAlerts = useMemo(
    () =>
      ArrayUtils.groupBy<Notification>(
        dashboardNotificationStore.data.filter((notification) => !notification.isRead),
        (n) => n.alertType!,
      ),
    [dashboardNotificationStore.data],
  );
  const isHasAnyNotifications = useMemo(() => Object.keys(groupedAlerts).length > 0, [groupedAlerts]);
  return (
    <Modal isOpen={props.isOpen} onClose={props.onClose} title={t('layout.notifications.title')} width='332px'>
      {!isHasAnyNotifications && <NoData text={t('layout.notifications.noData')} />}
      {Object.keys(groupedAlerts).map((k, idx) => {
        const group = groupedAlerts[k];
        const firstGroup = group[0];
        const title = firstGroup.alertTypeDisplayString;
        const text = firstGroup.text;
        const date = firstGroup.createdOn;

        return (
          <div key={idx} className='mb-4 flex w-full flex-col'>
            <div className='flex flex-col'>
              <div className='flex'>
                <div className='my-auto mr-1 h-3 w-3 rounded-full bg-primary' />
                <Text size='sm'>{title}</Text>
              </div>
              <Text size='xs' color='gray'>
                {text}
              </Text>
              <Text size='xs' color='gray'>
                {DateUtils.formatDate(date, { withTime: true })}
              </Text>
            </div>
            <Button color='outlined' to={RoutePaths.alerts.onNavigate({ id: groupedAlerts[k][0].alertGuid })}>
              {t('layout.notifications.buttons.viewAll')}
            </Button>
          </div>
        );
      })}
      {isHasAnyNotifications && (
        <div className='flex justify-between'>
          {profileFeatureStore.data?.settings.deleteNotifications && (
            <>
              <Button color='outlined' fullWidth onClick={onClearAllNotifications}>
                {t('layout.notifications.buttons.clearAll')}
              </Button>
              <div className='h-full w-6'></div>
            </>
          )}
          <Button color='outlined' fullWidth onClick={onMarkAllAsReadNotifications}>
            {t('layout.notifications.buttons.markAllRead')}
          </Button>
        </div>
      )}
      <Button className='mt-6' color='primary' fullWidth to={RoutePaths.alerts.onNavigate()}>
        {t('layout.notifications.buttons.history')}
      </Button>
    </Modal>
  );
};
